import { Button } from "@/components/ui/button";
import DateTimePicker from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type CalendarEventFormValues,
  calendarEventFormSchema,
  eventTypes,
} from "@/schemas/calendar";
import { zodResolver } from "@hookform/resolvers/zod";
import { addHours, differenceInMilliseconds } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface AddEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CalendarEventFormValues) => void;
  selectedDate: Date;
  defaultValues?: Partial<CalendarEventFormValues> & {
    duration?: number;
  };
}

function roundToNearest15Minutes(date: Date): Date {
  const minutes = date.getMinutes();
  const remainder = minutes % 15;
  const roundedMinutes =
    remainder < 8 ? minutes - remainder : minutes + (15 - remainder);
  const rounded = new Date(date);
  rounded.setMinutes(roundedMinutes);
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);
  return rounded;
}

export function AddEventDialog({
  open,
  onOpenChange,
  onSubmit,
  selectedDate,
  defaultValues,
}: AddEventDialogProps) {
  const [lastDuration, setLastDuration] = useState<number>(
    defaultValues?.duration
      ? defaultValues.duration * 60000 // Convert minutes to milliseconds
      : 3600000, // Default 1 hour in milliseconds
  );

  // Round the selectedDate to nearest 15 minutes
  const roundedSelectedDate = roundToNearest15Minutes(selectedDate);

  const form = useForm<CalendarEventFormValues>({
    resolver: zodResolver(calendarEventFormSchema),
    defaultValues: {
      title: defaultValues?.title ?? "",
      type: defaultValues?.type ?? "other",
      description: defaultValues?.description ?? "",
      location: "",
      start_at: roundedSelectedDate,
      end_at: defaultValues?.duration
        ? new Date(
            roundedSelectedDate.getTime() + defaultValues.duration * 60000,
          )
        : addHours(roundedSelectedDate, 1),
      is_all_day: false,
    },
  });

  useEffect(() => {
    const start = form.getValues("start_at");
    const end = form.getValues("end_at");

    const currentDuration = differenceInMilliseconds(end, start);
    if (currentDuration > 0) {
      setLastDuration(currentDuration);
    }

    const roundedDate = roundToNearest15Minutes(selectedDate);
    form.reset({
      ...form.getValues(),
      start_at: roundedDate,
      end_at: new Date(roundedDate.getTime() + lastDuration),
    });
  }, [selectedDate, form, lastDuration]);

  const startDate = form.watch("start_at");
  useEffect(() => {
    const end = form.getValues("end_at");
    const currentDuration = differenceInMilliseconds(end, startDate);

    if (currentDuration <= 0) {
      form.setValue("end_at", new Date(startDate.getTime() + lastDuration));
    } else {
      setLastDuration(currentDuration);
    }
  }, [startDate, form, lastDuration]);

  const endDate = form.watch("end_at");
  useEffect(() => {
    const start = form.getValues("start_at");
    if (endDate <= start) {
      form.setValue("end_at", new Date(start.getTime() + lastDuration));
    } else {
      setLastDuration(differenceInMilliseconds(endDate, start));
    }
  }, [endDate, form, lastDuration]);

  // Update handlers to handle undefined case
  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;
    const roundedDate = roundToNearest15Minutes(date);
    form.setValue("start_at", roundedDate);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) return;
    const roundedDate = roundToNearest15Minutes(date);
    form.setValue("end_at", roundedDate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Event</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter event title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type
                            .replace("_", " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <FormField
                control={form.control}
                name="start_at"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start</FormLabel>
                    <FormControl>
                      <DateTimePicker
                        value={field.value}
                        onChange={handleStartDateChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_at"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>End</FormLabel>
                    <FormControl>
                      <DateTimePicker
                        value={field.value}
                        onChange={handleEndDateChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter location" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter event description"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit">Create Event</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
